import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Heart, Shield, Star, Clock, Award, Users, Mail, MapPin, Navigation } from 'lucide-react';
import { cabinetInfo } from '../data/mock';

const HomePage = () => {
  const { doctors, assistants, contact } = cabinetInfo;
  return (
    <div>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--bg-primary) 0%, rgba(250, 249, 246, 0.9) 50%, rgba(108, 166, 193, 0.1) 100%)',
        minHeight: '95vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, var(--accent-secondary), transparent)',
          opacity: '0.3'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '8%',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--accent-primary), transparent)',
          opacity: '0.2'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '15%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(225deg, var(--accent-secondary), transparent)',
          opacity: '0.25'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '25%',
          right: '12%',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'linear-gradient(315deg, var(--accent-primary), transparent)',
          opacity: '0.15'
        }}></div>

        <div className="container">
          <div className="fade-in-up">
            {/* Main Logo/Title */}
            <h1 className="hero-large" style={{
              marginBottom: '3rem',
              transform: 'scale(1.1)',
              animation: 'logoFloat 6s ease-in-out infinite'
            }}>
              drs.
            </h1>
            
            {/* Subtitle */}
            <p className="hero-medium" style={{
              marginBottom: '2rem',
              color: 'var(--text-secondary)',
              fontWeight: '400'
            }}>
              Un sourire en toute confiance
            </p>
            
            {/* Description */}
            <div style={{ maxWidth: '700px', margin: '0 auto 3.5rem' }}>
              <p className="body-large" style={{
                color: 'var(--text-secondary)',
                marginBottom: '1.5rem',
                fontSize: '1.25rem',
                lineHeight: '1.8'
              }}>
                Bienvenue au cabinet dentaire des <strong style={{color: 'var(--accent-primary)'}}>Dr. Stéphanie Bruno</strong> et <strong style={{color: 'var(--accent-secondary)'}}>Dr. Sylvie Brutus</strong>
              </p>
              <p className="body-regular" style={{
                color: 'var(--text-secondary)',
                fontSize: '1.1rem'
              }}>
                Nous vous accompagnons avec bienveillance pour prendre soin de votre santé bucco-dentaire dans notre cabinet moderne situé à Marseille.
              </p>
            </div>
            
            {/* CTA Buttons */}
            <div style={{
              display: 'flex',
              gap: '2rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '5rem'
            }}>
              <a
                href="tel:04-91-76-16-73"
                className="btn-call"
                style={{ 
                  fontSize: '1.2rem',
                  padding: '20px 40px',
                  boxShadow: '0 8px 25px rgba(108, 166, 193, 0.4)',
                  transform: 'scale(1.05)'
                }}
              >
                <Phone size={22} />
                Prendre rendez-vous
              </a>
            </div>

            {/* Trust badges */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '3rem',
              flexWrap: 'wrap',
              opacity: '0.8'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: 'var(--accent-primary)',
                fontSize: '1rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: 'var(--accent-primary)'
                }}></div>
                15+ ans d'expérience
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: 'var(--accent-secondary)',
                fontSize: '1rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: 'var(--accent-secondary)'
                }}></div>
                Technologies modernes
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: 'var(--accent-primary)',
                fontSize: '1rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: 'var(--accent-primary)'
                }}></div>
                Cabinet à Marseille
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Presentation Section - Moved from About page */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1">Présentation du cabinet</h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '700px', 
              margin: '0 auto 3rem'
            }}>
              Découvrez l'équipe médicale qui vous accompagne avec professionnalisme 
              et bienveillance dans vos soins dentaires.
            </p>
          </div>
          
          {/* Doctors */}
          <div style={{ marginBottom: '5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h3 className="heading-2" style={{ color: 'var(--accent-primary)' }}>
                Nos practiciennes
              </h3>
            </div>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4rem'
            }}>
              {doctors.map((doctor, index) => (
                <div key={doctor.id} style={{
                  textAlign: 'center',
                  padding: '3rem 2rem',
                  background: 'var(--bg-primary)',
                  borderRadius: '20px',
                  boxShadow: '0 8px 30px var(--shadow)'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2rem'
                  }}>
                    <div>
                      <h4 className="heading-3" style={{ 
                        color: 'var(--accent-primary)',
                        marginBottom: '0.5rem'
                      }}>
                        {doctor.name}
                      </h4>
                      <p className="body-regular" style={{
                        color: 'var(--text-secondary)',
                        fontWeight: '500',
                        marginBottom: '1.5rem'
                      }}>
                        {doctor.title}
                      </p>
                      <p className="body-regular" style={{
                        color: 'var(--text-primary)',
                        lineHeight: '1.6',
                        maxWidth: '400px'
                      }}>
                        À l'aise avec toutes les spécialités dentaires pour vous offrir des soins complets et personnalisés.
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Assistants */}
          <div>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h3 className="heading-2" style={{ color: 'var(--accent-secondary)' }}>
                Notre équipe d'assistantes
              </h3>
              <p className="body-large" style={{ 
                color: 'var(--text-secondary)', 
                maxWidth: '600px', 
                margin: '0 auto' 
              }}>
                Des assistantes qualifiées qui vous accueillent et vous accompagnent tout au long de vos soins
              </p>
            </div>
            
            <div className="grid-two-column" style={{ gap: '3rem' }}>
              {assistants.map((assistant) => (
                <div key={assistant.id} style={{
                  textAlign: 'center',
                  padding: '2.5rem 2rem',
                  background: 'var(--bg-primary)',
                  borderRadius: '20px',
                  boxShadow: '0 8px 30px var(--shadow)',
                  border: '2px solid var(--accent-secondary)'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '1.5rem'
                  }}>
                    <div>
                      <h4 className="heading-3" style={{ 
                        color: 'var(--accent-secondary)',
                        marginBottom: '0.5rem'
                      }}>
                        {assistant.name}
                      </h4>
                      <p className="body-regular" style={{
                        color: 'var(--text-secondary)',
                        fontWeight: '500',
                        marginBottom: '1rem'
                      }}>
                        {assistant.title}
                      </p>
                      <p className="body-regular" style={{
                        color: 'var(--text-primary)',
                        lineHeight: '1.6',
                        maxWidth: '350px'
                      }}>
                        Professionnelle et attentionnée, elle veille à votre confort et vous guide avec bienveillance durant votre visite.
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="section-padding" style={{
        background: 'var(--white)'
      }}>
        <div className="container">
          <div className="grid-three-column">
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Heart size={36} color="white" />
              </div>
              <h3 className="heading-3">Approche bienveillante</h3>
              <p className="body-regular">
                Nous privilégions l'écoute et le confort de nos patients pour des soins sans stress.
              </p>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, var(--accent-secondary), var(--accent-primary))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Shield size={36} color="white" />
              </div>
              <h3 className="heading-3">Expertise reconnue</h3>
              <p className="body-regular">
                Nos praticiens allient techniques modernes et expérience pour des soins de qualité.
              </p>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Star size={36} color="white" />
              </div>
              <h3 className="heading-3">Suivi personnalisé</h3>
              <p className="body-regular">
                Chaque patient bénéficie d'un plan de soins adapté à ses besoins spécifiques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Services Preview */}
      <section className="section-padding" style={{
        background: 'var(--bg-primary)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1">Nos domaines d'expertise</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Des soins complets pour toute la famille, dans un environnement chaleureux et moderne.
            </p>
          </div>

          <div className="grid-two-column">
            <div>
              <h3 className="heading-2" style={{ color: 'var(--accent-primary)', marginBottom: '1.5rem' }}>
                Soins préventifs & courants
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <li className="body-regular" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border-light)' }}>
                  • Consultations et bilans dentaires
                </li>
                <li className="body-regular" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border-light)' }}>
                  • Détartrage et prophylaxie
                </li>
                <li className="body-regular" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border-light)' }}>
                  • Soins des caries
                </li>
                <li className="body-regular" style={{ padding: '0.5rem 0' }}>
                  • Traitements de gencives
                </li>
              </ul>
            </div>

            <div>
              <h3 className="heading-2" style={{ color: 'var(--accent-secondary)', marginBottom: '1.5rem' }}>
                Soins esthétiques & spécialisés
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <li className="body-regular" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border-light)' }}>
                  • Blanchiment dentaire
                </li>
                <li className="body-regular" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border-light)' }}>
                  • Facettes et couronnes
                </li>
                <li className="body-regular" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border-light)' }}>
                  • Soins esthétiques
                </li>
                <li className="body-regular" style={{ padding: '0.5rem 0' }}>
                  • Prothèses dentaires
                </li>
              </ul>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <a
              href="tel:04-91-76-16-73"
              className="btn-call"
              style={{ fontSize: '1.1rem' }}
            >
              <Phone size={20} />
              Contactez-nous pour plus d'infos
            </a>
          </div>
        </div>
      </section>

      {/* Contact CTA - Moved to end */}
      <section className="section-padding" style={{
        background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
        color: 'var(--white)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-1" style={{ color: 'var(--white)', marginBottom: '1rem' }}>
            Prêt à retrouver le sourire ?
          </h2>
          <p className="body-large" style={{ 
            color: 'rgba(255, 255, 255, 0.9)', 
            marginBottom: '2.5rem',
            maxWidth: '500px',
            margin: '0 auto 2.5rem'
          }}>
            Contactez-nous dès maintenant pour planifier votre consultation dans notre cabinet à Marseille.
          </p>
          
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a
              href="tel:04-91-76-16-73"
              style={{
                background: 'var(--white)',
                color: 'var(--accent-primary)',
                border: '2px solid var(--white)',
                borderRadius: '25px',
                padding: '15px 30px',
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
                minWidth: '220px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'var(--white)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'var(--white)';
                e.target.style.color = 'var(--accent-primary)';
              }}
            >
              <Phone size={20} />
              04 91 76 16 73
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section - Moved from Contact page */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1">Nous contacter</h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '600px', 
              margin: '0 auto'
            }}>
              Prenez rendez-vous ou contactez-nous pour toute question. 
              Notre équipe est à votre disposition.
            </p>
          </div>

          <div className="grid-two-column">
            {/* Contact Details */}
            <div>
              <h3 className="heading-2" style={{ 
                color: 'var(--accent-primary)',
                marginBottom: '2rem'
              }}>
                Informations de contact
              </h3>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2rem'
              }}>
                {/* Phone */}
                <div className="card" style={{
                  padding: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  border: '2px solid var(--accent-primary)',
                  borderRadius: '15px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Phone size={24} color="white" />
                  </div>
                  
                  <div>
                    <h4 className="heading-3" style={{
                      fontSize: '1rem',
                      marginBottom: '0.25rem',
                      color: 'var(--text-primary)'
                    }}>
                      Téléphone
                    </h4>
                    <a 
                      href="tel:04-91-76-16-73"
                      className="body-large"
                      style={{
                        color: 'var(--accent-primary)',
                        textDecoration: 'none',
                        fontWeight: '600'
                      }}
                    >
                      {contact.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="card" style={{
                  padding: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, var(--accent-secondary), var(--accent-primary))',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Mail size={24} color="white" />
                  </div>
                  
                  <div>
                    <h4 className="heading-3" style={{
                      fontSize: '1rem',
                      marginBottom: '0.25rem',
                      color: 'var(--text-primary)'
                    }}>
                      Email
                    </h4>
                    <a 
                      href={`mailto:${contact.email}`}
                      className="body-regular"
                      style={{
                        color: 'var(--accent-secondary)',
                        textDecoration: 'none',
                        fontWeight: '500',
                        wordBreak: 'break-all'
                      }}
                    >
                      {contact.email}
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="card" style={{
                  padding: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <MapPin size={24} color="white" />
                  </div>
                  
                  <div>
                    <h4 className="heading-3" style={{
                      fontSize: '1rem',
                      marginBottom: '0.25rem',
                      color: 'var(--text-primary)'
                    }}>
                      Adresse
                    </h4>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      {contact.address.full}
                    </p>
                  </div>
                </div>

                {/* Call to Action */}
                <div style={{ marginTop: '1rem' }}>
                  <a
                    href="tel:04-91-76-16-73"
                    className="btn-call"
                    style={{ width: '100%', fontSize: '1.1rem' }}
                  >
                    <Phone size={20} />
                    Appeler maintenant
                  </a>
                </div>
              </div>
            </div>

            {/* Map & Appointment Info */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem'
            }}>
              {/* Appointment Info */}
              <div className="card" style={{
                background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                color: 'var(--white)'
              }}>
                <h4 className="heading-3" style={{ 
                  color: 'var(--white)', 
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <Clock size={24} />
                  Prise de rendez-vous
                </h4>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <p className="body-regular" style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: '1.6',
                    textAlign: 'center'
                  }}>
                    Contactez-nous par téléphone pour planifier votre consultation. Nous vous proposerons un créneau adapté à votre emploi du temps.
                  </p>
                  
                  <a
                    href="tel:04-91-76-16-73"
                    style={{
                      background: 'var(--white)',
                      color: 'var(--accent-primary)',
                      border: 'none',
                      borderRadius: '25px',
                      padding: '15px 30px',
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      transition: 'all 0.3s ease',
                      minWidth: '250px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = '0 4px 20px rgba(255,255,255,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <Phone size={22} />
                    04 91 76 16 73
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{
                  padding: '1.5rem',
                  background: 'var(--bg-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  borderBottom: '1px solid var(--border-light)'
                }}>
                  <Navigation size={24} color="var(--accent-primary)" />
                  <h4 className="heading-3" style={{ 
                    fontSize: '1rem',
                    color: 'var(--text-primary)'
                  }}>
                    Plan d'accès
                  </h4>
                </div>
                
                <div style={{ 
                  height: '300px',
                  background: 'var(--text-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2903.123456789!2d5.3806!3d43.2485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12c9c123456789ab%3A0x123456789abcdef!2s21%20Avenue%20de%20Mazargues%2C%2013008%20Marseille%2C%20France!5e0!3m2!1sfr!2sfr!4v1234567890123!5m2!1sfr!2sfr"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Plan d'accès au cabinet dentaire"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Information */}
      <section className="section-padding" style={{
        background: 'var(--bg-primary)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h3 className="heading-2" style={{
              color: 'var(--accent-primary)',
              marginBottom: '1.5rem'
            }}>
              Urgences dentaires
            </h3>
            
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.7'
            }}>
              En cas d'urgence dentaire (douleur intense, traumatisme, gonflement), 
              n'hésitez pas à nous contacter immédiatement. Nous ferons notre possible 
              pour vous recevoir dans les plus brefs délais.
            </p>
            
            <div className="card" style={{
              background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
              color: 'var(--white)',
              padding: '2rem',
              textAlign: 'center',
              border: '3px solid #c0392b'
            }}>
              <h4 className="heading-3" style={{ 
                color: 'var(--white)', 
                marginBottom: '1rem'
              }}>
                Numéro d'urgence
              </h4>
              
              <a
                href="tel:04-91-76-16-73"
                style={{
                  background: 'var(--white)',
                  color: '#e74c3c',
                  border: 'none',
                  borderRadius: '25px',
                  padding: '15px 30px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  minWidth: '250px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 4px 20px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <Phone size={22} />
                04 91 76 16 73
              </a>
              
              <p className="body-small" style={{
                color: 'rgba(255, 255, 255, 0.9)',
                marginTop: '1rem'
              }}>
                Laissez un message si nous ne pouvons répondre immédiatement
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cabinet Values */}
      <section className="section-padding" style={{
        background: 'var(--bg-primary)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1">Nos valeurs</h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '600px', 
              margin: '0 auto' 
            }}>
              Ce qui nous guide au quotidien dans notre pratique
            </p>
          </div>

          <div className="grid-three-column">
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Heart size={36} color="white" />
              </div>
              <h3 className="heading-3">Bienveillance</h3>
              <p className="body-regular">
                Nous accordons une attention particulière au confort et au bien-être 
                de chaque patient, dans un environnement chaleureux et rassurant.
              </p>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, var(--accent-secondary), var(--accent-primary))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Award size={36} color="white" />
              </div>
              <h3 className="heading-3">Excellence</h3>
              <p className="body-regular">
                Notre engagement constant vers l'excellence nous pousse à nous former 
                régulièrement aux dernières innovations dentaires.
              </p>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Users size={36} color="white" />
              </div>
              <h3 className="heading-3">Écoute</h3>
              <p className="body-regular">
                Nous prenons le temps d'écouter vos préoccupations et de répondre 
                à toutes vos questions avec patience et pédagogie.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;